import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Building2, ArrowUpDown, Users, DollarSign, Briefcase } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Mock data
const MOCK_CUSTOMERS = [
  {
    id: "cust-001",
    name: "Whole Foods Market",
    industry: "Retail",
    website: "www.wholefoodsmarket.com",
    address: "Austin, TX",
    phone: "(512) 477-4455",
    email: "buyers@wholefoodsmarket.com",
    notes: "Premium natural and organic foods retailer. Key contact is Sarah Johnson.",
    opportunity_count: 2,
    total_pipeline: 650000,
    contact_count: 2,
    status: "active",
    created_at: "2024-06-15"
  },
  {
    id: "cust-002",
    name: "Costco Wholesale",
    industry: "Wholesale",
    website: "www.costco.com",
    address: "Issaquah, WA",
    phone: "(425) 313-8100",
    email: "vendors@costco.com",
    notes: "Membership warehouse club. Looking for club-size packs.",
    opportunity_count: 1,
    total_pipeline: 680000,
    contact_count: 1,
    status: "active",
    created_at: "2024-07-20"
  },
  {
    id: "cust-003",
    name: "Target Corporation",
    industry: "Retail",
    website: "www.target.com",
    address: "Minneapolis, MN",
    phone: "(612) 304-6073",
    email: "vendors@target.com",
    notes: "National retailer. Interested in regional test programs.",
    opportunity_count: 1,
    total_pipeline: 225000,
    contact_count: 0,
    status: "active",
    created_at: "2024-08-10"
  },
  {
    id: "cust-004",
    name: "Sysco Foods",
    industry: "Food Service",
    website: "www.sysco.com",
    address: "Houston, TX",
    phone: "(281) 584-1390",
    email: "vendors@sysco.com",
    notes: "Food service distribution. Recent deal closed.",
    opportunity_count: 1,
    total_pipeline: 350000,
    contact_count: 1,
    status: "active",
    created_at: "2024-05-01"
  },
  {
    id: "cust-005",
    name: "Kroger",
    industry: "Retail",
    website: "www.kroger.com",
    address: "Cincinnati, OH",
    phone: "(513) 762-4000",
    email: "vendors@kroger.com",
    notes: "Large grocery chain. Potential private label partner.",
    opportunity_count: 1,
    total_pipeline: 320000,
    contact_count: 2,
    status: "active",
    created_at: "2024-09-15"
  },
  {
    id: "cust-006",
    name: "Publix Super Markets",
    industry: "Retail",
    website: "www.publix.com",
    address: "Lakeland, FL",
    phone: "(863) 688-1188",
    email: "vendors@publix.com",
    notes: "Southeast regional grocery chain.",
    opportunity_count: 0,
    total_pipeline: 0,
    contact_count: 1,
    status: "prospect",
    created_at: "2024-10-01"
  },
  {
    id: "cust-007",
    name: "US Foods",
    industry: "Food Service",
    website: "www.usfoods.com",
    address: "Rosemont, IL",
    phone: "(847) 720-8000",
    email: "vendors@usfoods.com",
    notes: "Food service distributor. Initial discussions.",
    opportunity_count: 0,
    total_pipeline: 0,
    contact_count: 0,
    status: "prospect",
    created_at: "2024-11-01"
  }
];

const INDUSTRIES = ["All Industries", "Retail", "Wholesale", "Food Service", "E-Commerce", "Other"];
const STATUSES = ["All Status", "active", "prospect", "inactive"];

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [industryFilter, setIndustryFilter] = useState("All Industries");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    industry: "",
    website: "",
    address: "",
    phone: "",
    email: "",
    notes: "",
    broker_id: "",
    broker_name: ""
  });

  useEffect(() => {
    fetchCustomers();
    fetchBrokers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/crm/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers', error);
      setCustomers(MOCK_CUSTOMERS);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/crm/brokers`);
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers', error);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      await axios.post(`${API}/crm/customers`, newCustomer);
      toast.success("Customer created");
      setShowCreateModal(false);
      setNewCustomer({ name: "", industry: "", website: "", address: "", phone: "", email: "", notes: "" });
      fetchCustomers();
    } catch (error) {
      console.error('Failed to create customer', error);
      // Add to local state for demo
      const newCust = {
        ...newCustomer,
        id: `cust-${Date.now()}`,
        opportunity_count: 0,
        total_pipeline: 0,
        contact_count: 0,
        status: "prospect",
        created_at: new Date().toISOString().split('T')[0]
      };
      setCustomers(prev => [...prev, newCust]);
      toast.success("Customer created");
      setShowCreateModal(false);
      setNewCustomer({ name: "", industry: "", website: "", address: "", phone: "", email: "", notes: "" });
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatRevenue = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}mm`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}k`;
    return `$${amount || 0}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      prospect: 'bg-blue-100 text-blue-700',
      inactive: 'bg-gray-100 text-gray-700'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const filteredCustomers = customers
    .filter(customer => {
      const matchesSearch = searchQuery === "" || 
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.address?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesIndustry = industryFilter === "All Industries" || customer.industry === industryFilter;
      const matchesStatus = statusFilter === "All Status" || customer.status === statusFilter;
      return matchesSearch && matchesIndustry && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'total_pipeline' || sortField === 'opportunity_count' || sortField === 'contact_count') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Summary stats
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter(c => c.status === 'active').length;
  const totalPipeline = customers.reduce((sum, c) => sum + (c.total_pipeline || 0), 0);
  const totalOpportunities = customers.reduce((sum, c) => sum + (c.opportunity_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Building2 className="w-8 h-8" />
            Customers
          </h1>
          <p className="text-gray-600">Manage your customer relationships</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Customers</p>
                <p className="text-2xl font-bold">{totalCustomers}</p>
              </div>
              <Building2 className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Customers</p>
                <p className="text-2xl font-bold text-green-600">{activeCustomers}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Pipeline</p>
                <p className="text-2xl font-bold text-blue-600">{formatRevenue(totalPipeline)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open Opportunities</p>
                <p className="text-2xl font-bold text-purple-600">{totalOpportunities}</p>
              </div>
              <Briefcase className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map(industry => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map(status => (
                <SelectItem key={status} value={status}>
                  {status === "All Status" ? status : status.charAt(0).toUpperCase() + status.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Customers Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Customer Name
                      {sortField === 'name' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('industry')}
                  >
                    <div className="flex items-center gap-1">
                      Industry
                      {sortField === 'industry' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('address')}
                  >
                    <div className="flex items-center gap-1">
                      Location
                      {sortField === 'address' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortField === 'status' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('opportunity_count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Opportunities
                      {sortField === 'opportunity_count' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_pipeline')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Pipeline
                      {sortField === 'total_pipeline' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('contact_count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Contacts
                      {sortField === 'contact_count' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr 
                      key={customer.id} 
                      onClick={() => navigate(`/crm/customers/${customer.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-indigo-600 font-semibold text-sm">
                              {customer.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{customer.name}</div>
                            <div className="text-xs text-gray-500">{customer.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{customer.industry || '-'}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {customer.address || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(customer.status)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${customer.opportunity_count > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                          {customer.opportunity_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${customer.total_pipeline > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {formatRevenue(customer.total_pipeline)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-600">{customer.contact_count || 0}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Customer Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="Company name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Industry</Label>
                <Select 
                  value={newCustomer.industry} 
                  onValueChange={(val) => setNewCustomer({ ...newCustomer, industry: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.filter(i => i !== "All Industries").map(industry => (
                      <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={newCustomer.website}
                  onChange={(e) => setNewCustomer({ ...newCustomer, website: e.target.value })}
                  placeholder="www.example.com"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                placeholder="City, State"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  placeholder="contact@company.com"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newCustomer.notes}
                onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
            <div>
              <Label>Primary Broker</Label>
              <Select 
                value={newCustomer.broker_id || "none"} 
                onValueChange={(val) => {
                  const broker = brokers.find(b => b.id === val);
                  setNewCustomer({ 
                    ...newCustomer, 
                    broker_id: val === "none" ? "" : val,
                    broker_name: broker?.name || ""
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select broker (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No broker assigned</SelectItem>
                  {brokers.map(broker => (
                    <SelectItem key={broker.id} value={broker.id}>
                      {broker.name} ({broker.commission_rate}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer} disabled={!newCustomer.name}>
              Add Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
